/**
 * PixBimViewerV2 – deck.gl-based drop-in replacement for PixBimViewer.
 *
 * Props interface is intentionally identical so callers only need to swap the import.
 * All geometry rendering runs on the GPU via WebGL/WebGPU (deck.gl).
 *
 * Coordinate system:
 *   Input points are in model space [x, y].
 *   We apply the same flip ([x,y]→[y,x]) and clockwise rotation that PixBimViewer
 *   used internally, so the visual output is equivalent.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DeckGL from "@deck.gl/react";
import type { DeckGLRef } from "@deck.gl/react";
import { OrthographicView } from "@deck.gl/core";
import type { PickingInfo } from "@deck.gl/core";
import {
  SolidPolygonLayer,
  PolygonLayer,
  PathLayer,
  LineLayer,
  ScatterplotLayer,
  TextLayer,
} from "@deck.gl/layers";

import type {
  IPixBimCameraState,
  IPixCircle,
  IPixPath,
  IPixPolygon,
  MlHeatmapTile,
  PixBimViewerAction,
} from "./types";
import { getBoundingBox } from "./geo";
import { hexToRgba, parseStrokeWidth, parseFontSize } from "./colorUtils";
import {
  computeTransformedData,
  computeFitZoom,
  bboxCenter,
  polygonCentroid,
  invertTransform,
  applyTransform,
} from "./transformUtils";
import type { Pos2, TransformedPolygon } from "./transformUtils";

// ---------------------------------------------------------------------------
// Types (identical to PixBimViewer public API)
// ---------------------------------------------------------------------------

export type {
  IPixBimCameraState,
  IPixCircle,
  IPixPath,
  IPixPolygon,
  MlHeatmapTile,
  PixBimViewerAction,
};

interface PixBimViewerV2Props {
  polygons: IPixPolygon[];
  paths?: IPixPath[];
  circles?: IPixCircle[];
  mlHeatmapTiles?: MlHeatmapTile[];
  angle?: number;
  onMoveStart?: (id: string) => void;
  onClick?: (id: string | undefined, coordinates: [number, number]) => void;
  onDragAndDrop?: (id: string, points: number[][]) => void;
  onRightMenuClick?: (action: string, polygon: IPixPolygon) => void;
  onDragSelected?: (ids: string[]) => void;
  pointerTracker?: { color: string; size: number };
  goToPolygon?: string;
  action?: PixBimViewerAction;
  zoomExtentKey?: string | number | null;
  useLeftClickToPan?: boolean;
  zoom?: number;
  centerWorld?: { x: number; y: number };
  onCameraChange?: (camera: IPixBimCameraState) => void;
}

// ---------------------------------------------------------------------------
// Internal types
// ---------------------------------------------------------------------------

interface ViewState {
  target: [number, number, number];
  zoom: number;
  minZoom: number;
  maxZoom: number;
}

interface DragState {
  polygonId: string;
  originalPoints: Pos2[];
  startWorld: Pos2;
}

interface SelectState {
  startScreen: Pos2;
  currentScreen: Pos2;
}

interface TooltipState {
  texts: string[];
  x: number;
  y: number;
}

interface ContextMenuState {
  polygon: IPixPolygon;
  x: number;
  y: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DRAG_DELAY_MS = 120;
const CLICK_MOVE_TOLERANCE_PX = 4;

const SELECT_HIGHLIGHT: [number, number, number, number] = [100, 160, 255, 60];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const PixBimViewerV2: React.FC<PixBimViewerV2Props> = (props) => {
  const {
    polygons,
    paths,
    circles = [],
    mlHeatmapTiles = [],
    angle = 180,
    onClick,
    onDragAndDrop,
    onDragSelected,
    onMoveStart,
    onRightMenuClick,
    pointerTracker,
    goToPolygon,
    action = "normal",
    zoomExtentKey,
    useLeftClickToPan = false,
    zoom: controlledZoom,
    centerWorld: controlledCenter,
    onCameraChange,
  } = props;

  // ---- Container size (for fit-zoom) ----
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ w: 800, h: 600 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setContainerSize({ w: Math.max(1, width), h: Math.max(1, height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ---- Transform all geometry once ----
  const td = useMemo(
    () => computeTransformedData(polygons, paths ?? [], circles, angle),
    [polygons, paths, circles, angle]
  );

  // ---- Camera (view state) ----
  const initialViewState = useMemo<ViewState>(() => {
    const zoom = computeFitZoom(td.bbox, containerSize.w, containerSize.h);
    const [cx, cy] = bboxCenter(td.bbox);
    return { target: [cx, cy, 0], zoom, minZoom: -5, maxZoom: 20 };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // only on mount

  const [viewState, setViewState] = useState<ViewState>(initialViewState);

  const handleViewStateChange = useCallback(
    ({ viewState: vs }: { viewState: ViewState }) => {
      setViewState(vs);
      onCameraChange?.({
        zoom: vs.zoom,
        centerWorld: { x: vs.target[0], y: vs.target[1] },
      });
    },
    [onCameraChange]
  );

  // Controlled camera from props
  useEffect(() => {
    if (controlledZoom === undefined || controlledCenter === undefined) return;
    setViewState((prev) => ({
      ...prev,
      zoom: controlledZoom,
      target: [controlledCenter.x, controlledCenter.y, 0],
    }));
  }, [controlledZoom, controlledCenter]);

  // zoomExtentKey → fit all to view
  const lastZoomKeyRef = useRef<string | number | null | undefined>(undefined);
  useEffect(() => {
    if (zoomExtentKey == null) return;
    if (lastZoomKeyRef.current === zoomExtentKey) return;
    lastZoomKeyRef.current = zoomExtentKey;
    const zoom = computeFitZoom(td.bbox, containerSize.w, containerSize.h);
    const [cx, cy] = bboxCenter(td.bbox);
    setViewState((prev) => ({ ...prev, zoom, target: [cx, cy, 0] }));
  }, [zoomExtentKey, td.bbox, containerSize]);

  // goToPolygon → fly to specific polygon
  const lastGoToRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!goToPolygon || goToPolygon === lastGoToRef.current) return;
    lastGoToRef.current = goToPolygon;
    const p = td.polygons.find((p) => p.id === goToPolygon);
    if (!p) return;
    const pBbox = getBoundingBox(p.tPoints);
    const zoom = computeFitZoom(pBbox, containerSize.w, containerSize.h);
    const [cx, cy] = bboxCenter(pBbox);
    setViewState((prev) => ({ ...prev, zoom, target: [cx, cy, 0] }));
  }, [goToPolygon, td.polygons, containerSize]);

  // ---- Interaction state ----
  const deckRef = useRef<DeckGLRef>(null);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);

  // Measure
  const [measurePts, setMeasurePts] = useState<Pos2[]>([]);
  const [measureMouse, setMeasureMouse] = useState<Pos2 | null>(null);

  // Drag-select
  const [selectState, setSelectState] = useState<SelectState | null>(null);
  const selectStateRef = useRef<SelectState | null>(null);
  const preSelectedRef = useRef<string[]>([]);

  // Drag-and-drop
  const [dragOverride, setDragOverride] = useState<{ id: string; tPoints: Pos2[] } | null>(null);
  const dragStateRef = useRef<DragState | null>(null);
  const dragTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isDraggingRef = useRef(false);

  // Pointer tracking for click vs drag discrimination
  const pointerDownPosRef = useRef<Pos2 | null>(null);
  const isPointerDownRef = useRef(false);
  const movedRef = useRef(false);

  // Last hovered polygon (for context menu on right-click)
  const lastHoveredPolygonRef = useRef<IPixPolygon | null>(null);

  // Hovered id for highlight
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  // Whether a moveable polygon is hovered — disables dragPan so device drag works
  const [overMoveable, setOverMoveable] = useState(false);

  // Middle-mouse pan state
  const middlePanRef = useRef<{ screenX: number; screenY: number } | null>(null);

  // Space key for pan
  const [spacePan, setSpacePan] = useState(false);
  useEffect(() => {
    const onDown = (e: KeyboardEvent) => {
      if (e.code === "Space") setSpacePan(true);
    };
    const onUp = (e: KeyboardEvent) => {
      if (e.code === "Space") setSpacePan(false);
    };
    const onBlur = () => setSpacePan(false);
    window.addEventListener("keydown", onDown);
    window.addEventListener("keyup", onUp);
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("keydown", onDown);
      window.removeEventListener("keyup", onUp);
      window.removeEventListener("blur", onBlur);
    };
  }, []);

  // Clear measure points when leaving measure mode
  useEffect(() => {
    if (action !== "measure") {
      setMeasurePts([]);
      setMeasureMouse(null);
    }
  }, [action]);

  // ---- Viewport helper ----
  const getViewport = useCallback(() => {
    return (deckRef.current?.deck as any)?.getViewports?.()?.[0] ?? null;
  }, []);

  const unprojectScreen = useCallback(
    (screenX: number, screenY: number): Pos2 | null => {
      const vp = getViewport();
      if (!vp) return null;
      const w = vp.unproject([screenX, screenY]);
      return [w[0], w[1]];
    },
    [getViewport]
  );

  // ---- Drag-and-drop ----
  const startDrag = useCallback(
    (polygonId: string, screenX: number, screenY: number) => {
      const p = td.polygons.find((p) => p.id === polygonId);
      if (!p) return;
      const startWorld = unprojectScreen(screenX, screenY);
      if (!startWorld) return;
      onMoveStart?.(polygonId);
      isDraggingRef.current = true;
      dragStateRef.current = {
        polygonId,
        originalPoints: [...p.tPoints],
        startWorld,
      };
    },
    [td.polygons, unprojectScreen, onMoveStart]
  );

  const updateDrag = useCallback(
    (screenX: number, screenY: number) => {
      const ds = dragStateRef.current;
      if (!ds) return;
      const currentWorld = unprojectScreen(screenX, screenY);
      if (!currentWorld) return;
      const dx = currentWorld[0] - ds.startWorld[0];
      const dy = currentWorld[1] - ds.startWorld[1];
      const newPoints: Pos2[] = ds.originalPoints.map(([px, py]) => [px + dx, py + dy]);
      setDragOverride({ id: ds.polygonId, tPoints: newPoints });
    },
    [unprojectScreen]
  );

  const finishDrag = useCallback(() => {
    const ds = dragStateRef.current;
    if (!ds) return;
    isDraggingRef.current = false;
    dragStateRef.current = null;

    const finalTPoints =
      dragOverride?.id === ds.polygonId ? dragOverride.tPoints : ds.originalPoints;

    setDragOverride(null);

    // Convert back to original model space: un-rotate then un-flip
    const modelPoints = finalTPoints.map((tp) => invertTransform(tp, td.center, td.angleDeg));
    onDragAndDrop?.(ds.polygonId, modelPoints);
  }, [dragOverride, td.center, td.angleDeg, onDragAndDrop]);

  // ---- Build layer data (apply drag override) ----
  const polygonLayerData = useMemo(() => {
    if (!dragOverride) return td.polygons;
    return td.polygons.map((p) =>
      p.id === dragOverride.id ? { ...p, tPoints: dragOverride.tPoints } : p
    );
  }, [td.polygons, dragOverride]);

  // ---- Layers ----
  const layers = useMemo(() => {
    // console.time('[PixBimV2] layers useMemo');
    // Classify polygons: hollow = outline-only (white/no fill), filled = has a real fill color
    type PolyEdge = {
      from: Pos2;
      to: Pos2;
      strokeColor: string | undefined;
      strokeWidth: string | undefined;
    };
    const isHollow = (p: TransformedPolygon) => {
      const hex = p.fillColor?.replace("#", "").toLowerCase();
      return !hex || hex === "fff" || hex === "ffffff";
    };
    const hollowPolys = polygonLayerData.filter(isHollow);
    const filledPolys = polygonLayerData.filter((p) => !isHollow(p));

    const toEdges = (polys: TransformedPolygon[]): PolyEdge[] =>
      polys.flatMap((poly) =>
        poly.tPoints.map((pt, i) => ({
          from: pt,
          to: poly.tPoints[(i + 1) % poly.tPoints.length],
          strokeColor: poly.strokeColor,
          strokeWidth: poly.strokeWidth,
        }))
      );

    // 1. Room outlines (drawn first = bottom)
    const roomStrokeLayer = new LineLayer<PolyEdge>({
      id: "room-strokes",
      data: toEdges(hollowPolys),
      pickable: false,
      getSourcePosition: (d: PolyEdge) => [...d.from, 0] as [number, number, number],
      getTargetPosition: (d: PolyEdge) => [...d.to, 0] as [number, number, number],
      getColor: (d: PolyEdge) => hexToRgba(d.strokeColor, 1),
      getWidth: (d: PolyEdge) => parseStrokeWidth(d.strokeWidth, 1) / 100,
      widthUnits: "common",
      widthMinPixels: 0.5,
    });

    // 2a. Hollow polygon hit areas (transparent fill, pickable so rooms can be clicked)
    const hollowHitLayer = new SolidPolygonLayer<TransformedPolygon>({
      id: "hollow-hit",
      data: hollowPolys,
      pickable: true,
      filled: true,
      getPolygon: (d: TransformedPolygon) => d.tPoints,
      getFillColor: [0, 0, 0, 0],
      _normalize: true,
    });

    // 2b. Furniture fills (drawn after room outlines)
    const polygonFillLayer = new SolidPolygonLayer<TransformedPolygon>({
      id: "polygons",
      data: filledPolys,
      pickable: true,
      filled: true,
      getPolygon: (d: TransformedPolygon) => d.tPoints,
      getFillColor: (d: TransformedPolygon) => hexToRgba(d.fillColor, d.opacity ?? 1),
      _normalize: true,
    });

    // 3. Furniture outlines (drawn on top of fills)
    const furnitureStrokeLayer = new LineLayer<PolyEdge>({
      id: "furniture-strokes",
      data: toEdges(filledPolys),
      pickable: false,
      getSourcePosition: (d: PolyEdge) => [...d.from, 0] as [number, number, number],
      getTargetPosition: (d: PolyEdge) => [...d.to, 0] as [number, number, number],
      getColor: (d: PolyEdge) => hexToRgba(d.strokeColor, 1),
      getWidth: (d: PolyEdge) => parseStrokeWidth(d.strokeWidth, 1) / 100,
      widthUnits: "common",
      widthMinPixels: 0.5,
    });

    // --- Paths ---
    type TPath = (typeof td.paths)[0];
    type TCircle = (typeof td.circles)[0];
    type MeasurePt = { path: Pos2[] };

    const pathLayer = new PathLayer<TPath>({
      id: "paths",
      data: td.paths,
      pickable: true,
      getPath: (d: TPath) => d.tPoints,
      getColor: (d: TPath) => hexToRgba(d.strokeColor, 1),
      getWidth: (d: TPath) => parseStrokeWidth(d.strokeWidth, 1) / 100,
      widthUnits: "common",
      jointRounded: true,
      capRounded: true,
    });

    // --- Circles ---
    const circleLayer = new ScatterplotLayer<TCircle>({
      id: "circles",
      data: td.circles,
      pickable: true,
      getPosition: (d: TCircle) => [...d.tPos, 0] as [number, number, number],
      getRadius: (d: TCircle) => d.radius,
      getFillColor: (d: TCircle) => hexToRgba(d.color, d.opacity ?? 1),
      getLineColor: [80, 80, 80, 255],
      stroked: false,
    });

    const heatmapGeometry = mlHeatmapTiles.map((tile: MlHeatmapTile) => ({
      tPoints: tile.points.map((point: number[]) =>
        applyTransform(point, td.center, td.angleDeg)
      ) as Pos2[],
      color: tile.color,
    }));

    const heatmapLayer = new SolidPolygonLayer<{
      tPoints: Pos2[];
      color: [number, number, number, number];
    }>({
      id: "ml-heatmap",
      data: heatmapGeometry,
      pickable: false,
      filled: true,
      getPolygon: (d: { tPoints: Pos2[]; color: [number, number, number, number] }) => d.tPoints,
      getFillColor: (d: { color: [number, number, number, number] }) => d.color,
      _normalize: true,
    });

    // --- Text on polygons ---
    const polyTextData = polygonLayerData.filter(
      (d: TransformedPolygon) => d.text?.show && d.text.values?.length
    );
    const polygonTextLayer = new TextLayer<TransformedPolygon>({
      id: "polygon-labels",
      data: polyTextData,
      getPosition: (d: TransformedPolygon) =>
        [...polygonCentroid(d.tPoints), 0] as [number, number, number],
      getText: (d: TransformedPolygon) => (d.text.values ?? []).join("\n"),
      getColor: (d: TransformedPolygon) => hexToRgba(d.text.color, 1),
      getSize: (d: TransformedPolygon) => {
        // Base size from polygon bounding box (mirrors old PixBimPolygon sizeConstant logic).
        // text.fontSize is a multiplier: "1" = default, "2" = double, etc.
        const xs = d.tPoints.map((p) => p[0]);
        const ys = d.tPoints.map((p) => p[1]);
        const w = Math.max(...xs) - Math.min(...xs);
        const h = Math.max(...ys) - Math.min(...ys);
        const shortSide = Math.min(w, h);
        const base = Math.min(shortSide, 2.5) / 6;
        const scale = parseFontSize(d.text.fontSize, 1);
        return base * scale;
      },
      sizeUnits: "common",
      getTextAnchor: "middle",
      getAlignmentBaseline: "center",
      characterSet: "auto",
      pickable: false,
    });

    // --- Text on circles ---
    const circleTextData = td.circles.filter((d: TCircle) => d.text?.show && d.text.values?.length);
    const circleTextLayer = new TextLayer<TCircle>({
      id: "circle-labels",
      data: circleTextData,
      getPosition: (d: TCircle) => [...d.tPos, 0] as [number, number, number],
      getText: (d: TCircle) => (d.text!.values ?? []).join("\n"),
      getColor: (d: TCircle) => hexToRgba(d.text!.color, 1),
      getSize: (d: TCircle) => parseFontSize(d.text!.fontSize, 12) / 100,
      sizeUnits: "common",
      getTextAnchor: "middle",
      getAlignmentBaseline: "center",
      characterSet: "auto",
      pickable: false,
    });

    // --- Measure line ---
    const measureLinePts: MeasurePt[] = [];
    if (measurePts.length >= 1) {
      const end = measurePts.length === 2 ? measurePts[1] : (measureMouse ?? measurePts[0]);
      measureLinePts.push({ path: [measurePts[0], end] });
    }
    const measureLayer = new PathLayer<MeasurePt>({
      id: "measure",
      data: measureLinePts,
      getPath: (d: MeasurePt) => d.path,
      getColor: [220, 80, 80, 220],
      getWidth: 2,
      widthUnits: "pixels",
      pickable: false,
    });

    // --- Drag-select highlight overlay ---
    const preSelected = td.polygons.filter((p) => preSelectedRef.current.includes(p.id));
    const selectHighlightLayer = new PolygonLayer<TransformedPolygon>({
      id: "select-highlight",
      data: preSelected,
      getPolygon: (d: TransformedPolygon) => d.tPoints,
      getFillColor: SELECT_HIGHLIGHT,
      stroked: false,
      filled: true,
      pickable: false,
      _normalize: true,
      updateTriggers: { data: preSelectedRef.current },
    });

    const result = [
      roomStrokeLayer,
      hollowHitLayer,
      polygonFillLayer,
      furnitureStrokeLayer,
      pathLayer,
      circleLayer,
      heatmapLayer,
      polygonTextLayer,
      circleTextLayer,
      measureLayer,
      selectHighlightLayer,
    ];
    return result;
  }, [
    polygonLayerData,
    td.paths,
    td.circles,
    td.polygons,
    td.center,
    td.angleDeg,
    mlHeatmapTiles,
    hoveredId,
    measurePts,
    measureMouse,
  ]);

  // ---- deck.gl pick helpers ----
  const pickAtScreen = useCallback((x: number, y: number) => {
    return (deckRef.current?.deck as any)?.pickObject({ x, y, radius: 4 }) ?? null;
  }, []);

  const pickInRect = useCallback((x: number, y: number, w: number, h: number): PickingInfo[] => {
    return (
      (deckRef.current?.deck as any)?.pickObjects({
        x,
        y,
        width: Math.max(1, w),
        height: Math.max(1, h),
        layerIds: ["polygons", "hollow-hit"],
      }) ?? []
    );
  }, []);

  // ---- Hover ----
  const handleHover = useCallback((info: PickingInfo) => {
    if (!info.object) {
      setHoveredId(null);
      setTooltip(null);
      setOverMoveable(false);
      lastHoveredPolygonRef.current = null;
      return;
    }
    const obj = info.object as TransformedPolygon | IPixPath;
    const id = obj.id;
    setHoveredId(id);

    const poly = info.layer?.id === "polygons" ? (obj as TransformedPolygon) : null;
    if (poly) {
      lastHoveredPolygonRef.current = poly;
      setOverMoveable(poly.isMoveable ?? false);
      if (poly.tooltip) {
        setTooltip({ texts: [poly.tooltip], x: info.x + 16, y: info.y + 16 });
      } else {
        setTooltip(null);
      }
    } else {
      lastHoveredPolygonRef.current = null;
      const path = info.layer?.id === "paths" ? (obj as IPixPath) : null;
      if (path?.tooltip) {
        setTooltip({ texts: [path.tooltip], x: info.x + 16, y: info.y + 16 });
      } else {
        setTooltip(null);
      }
    }
  }, []);

  // ---- Click (deck.gl global) ----
  // Fires for both empty-canvas clicks and object clicks; we route accordingly.
  const handleDeckClick = useCallback(
    (info: PickingInfo) => {
      if (action === "measure" || action === "dragSelect") return;
      if (movedRef.current) return;
      if (spacePan) return;
      const modelCoordinates =
        Array.isArray(info.coordinate) && info.coordinate.length >= 2
          ? (invertTransform([info.coordinate[0], info.coordinate[1]], td.center, angle) as [number, number])
          : ([info.x, info.y] as [number, number]);
      if (!info.object) {
        onClick?.(undefined, modelCoordinates);
        return;
      }
      const obj = info.object as TransformedPolygon | IPixCircle;
      if ((obj as TransformedPolygon).isNotClickable) return;
      onClick?.(obj.id, modelCoordinates);
    },
    [action, angle, onClick, spacePan, td.center]
  );

  // ---- Controller options ----
  // Pan rules (mirrors old PixBimViewport behaviour):
  //   useLeftClickToPan (dev)  → left mouse always pans (deck.gl dragPan)
  //   spacePan                 → left mouse pans while Space held (deck.gl dragPan)
  //   otherwise                → middle mouse pans (manual, deck.gl dragPan off)
  // dragPan is also disabled during dragSelect / measure so those modes work correctly.
  const leftDragPanActive =
    (useLeftClickToPan || spacePan) &&
    action !== "dragSelect" &&
    action !== "measure" &&
    !overMoveable;
  const controllerOptions = useMemo(
    () => ({
      dragPan: leftDragPanActive,
      scrollZoom: true,
      doubleClickZoom: false,
      keyboard: false,
    }),
    [leftDragPanActive]
  );

  // ---- Container pointer events ----
  const getScreenPos = (e: React.PointerEvent | PointerEvent): Pos2 => {
    const rect = containerRef.current!.getBoundingClientRect();
    return [e.clientX - rect.left, e.clientY - rect.top];
  };

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      const [sx, sy] = getScreenPos(e);

      // Middle mouse button — start pan
      if (e.button === 1) {
        e.preventDefault();
        middlePanRef.current = { screenX: sx, screenY: sy };
        return;
      }

      pointerDownPosRef.current = [sx, sy];
      isPointerDownRef.current = true;
      movedRef.current = false;
      setContextMenu(null);

      if (action === "measure") {
        const world = unprojectScreen(sx, sy);
        if (!world) return;
        setMeasurePts((prev) => {
          if (prev.length === 0) return [world];
          return [prev[0], world]; // finalize measurement with second click
        });
        return;
      }

      if (action === "dragSelect") {
        selectStateRef.current = { startScreen: [sx, sy], currentScreen: [sx, sy] };
        setSelectState({ startScreen: [sx, sy], currentScreen: [sx, sy] });
        preSelectedRef.current = [];
        return;
      }

      // Normal mode: check for drag-and-drop
      const picked = pickAtScreen(sx, sy);
      const poly = picked?.object as TransformedPolygon | null;
      if (poly?.isMoveable) {
        dragTimerRef.current = setTimeout(() => {
          startDrag(poly.id, sx, sy);
        }, DRAG_DELAY_MS);
      }
    },
    [action, unprojectScreen, pickAtScreen, startDrag]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      const [sx, sy] = getScreenPos(e);

      // Middle-mouse pan
      if (middlePanRef.current) {
        const dx = sx - middlePanRef.current.screenX;
        const dy = sy - middlePanRef.current.screenY;
        middlePanRef.current = { screenX: sx, screenY: sy };
        const scale = 1 / Math.pow(2, viewState.zoom);
        setViewState((prev) => ({
          ...prev,
          target: [prev.target[0] - dx * scale, prev.target[1] - dy * scale, 0] as [
            number,
            number,
            number,
          ],
        }));
        return;
      }

      // Measure – live line endpoint
      if (action === "measure" && measurePts.length === 1) {
        const world = unprojectScreen(sx, sy);
        if (world) setMeasureMouse(world);
      }

      // Drag-select – update rect
      if (action === "dragSelect" && selectStateRef.current) {
        const next = { ...selectStateRef.current, currentScreen: [sx, sy] as Pos2 };
        selectStateRef.current = next;
        setSelectState(next);

        // GPU pick in rect for live highlight
        const { startScreen, currentScreen } = next;
        const rx = Math.min(startScreen[0], currentScreen[0]);
        const ry = Math.min(startScreen[1], currentScreen[1]);
        const rw = Math.abs(currentScreen[0] - startScreen[0]);
        const rh = Math.abs(currentScreen[1] - startScreen[1]);
        const picked = pickInRect(rx, ry, rw, rh);
        preSelectedRef.current = picked
          .map((p) => (p.object as TransformedPolygon)?.id)
          .filter(Boolean)
          .filter((id) => td.polygons.find((p) => p.id === id)?.isDragSelectable);
        return;
      }

      // Check movement for click vs drag discrimination
      if (isPointerDownRef.current && pointerDownPosRef.current) {
        const dx = sx - pointerDownPosRef.current[0];
        const dy = sy - pointerDownPosRef.current[1];
        if (dx * dx + dy * dy > CLICK_MOVE_TOLERANCE_PX * CLICK_MOVE_TOLERANCE_PX) {
          movedRef.current = true;
          // Don't cancel the drag timer on small movement — natural hand jitter would
          // otherwise prevent device dragging from ever starting.
        }
      }

      // Drag-and-drop move
      if (isDraggingRef.current) {
        updateDrag(sx, sy);
      }
    },
    [action, measurePts, unprojectScreen, pickInRect, td.polygons, updateDrag]
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (e.button === 1) {
        middlePanRef.current = null;
        return;
      }
      isPointerDownRef.current = false;

      if (dragTimerRef.current) {
        clearTimeout(dragTimerRef.current);
        dragTimerRef.current = null;
      }

      if (isDraggingRef.current) {
        finishDrag();
        return;
      }

      if (action === "dragSelect") {
        const ss = selectStateRef.current;
        if (ss) {
          const { startScreen, currentScreen } = ss;
          const rx = Math.min(startScreen[0], currentScreen[0]);
          const ry = Math.min(startScreen[1], currentScreen[1]);
          const rw = Math.abs(currentScreen[0] - startScreen[0]);
          const rh = Math.abs(currentScreen[1] - startScreen[1]);
          const picked = pickInRect(rx, ry, rw, rh);
          const ids = picked
            .map((p) => (p.object as TransformedPolygon)?.id)
            .filter(Boolean)
            .filter((id) => td.polygons.find((p) => p.id === id)?.isDragSelectable);
          onDragSelected?.(ids);
        }
        selectStateRef.current = null;
        setSelectState(null);
        preSelectedRef.current = [];
        return;
      }
    },
    [action, finishDrag, pickInRect, td.polygons, onDragSelected]
  );

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const poly = lastHoveredPolygonRef.current;
    if (poly?.rightClickMenu?.length) {
      const rect = containerRef.current!.getBoundingClientRect();
      setContextMenu({ polygon: poly, x: e.clientX - rect.left, y: e.clientY - rect.top });
    }
  }, []);

  // ---- Measure distance label ----
  const measureDistance = useMemo(() => {
    if (measurePts.length < 2) return null;
    const [a, b] = measurePts;
    // Distance is in model space units (meters typically)
    const dist = Math.sqrt((b[0] - a[0]) ** 2 + (b[1] - a[1]) ** 2);
    const midX = (a[0] + b[0]) / 2;
    const midY = (a[1] + b[1]) / 2;
    return { dist, midWorld: [midX, midY] as Pos2 };
  }, [measurePts]);

  const measureLabelScreen = useMemo(() => {
    if (!measureDistance) return null;
    const vp = getViewport();
    if (!vp) return null;
    const [sx, sy] = vp.project([...measureDistance.midWorld, 0]);
    return { x: sx, y: sy, dist: measureDistance.dist };
  }, [measureDistance, viewState, getViewport]); // viewState dep ensures recalc on pan/zoom

  // ---- Pointer tracker ----
  const [pointerPos, setPointerPos] = useState<Pos2 | null>(null);
  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!pointerTracker) return;
      const rect = containerRef.current!.getBoundingClientRect();
      setPointerPos([e.clientX - rect.left, e.clientY - rect.top]);
    },
    [pointerTracker]
  );
  const handleMouseLeave = useCallback(() => setPointerPos(null), []);

  // ---- Selection rect DOM style ----
  const selectRectStyle: React.CSSProperties | null = useMemo(() => {
    if (!selectState) return null;
    const { startScreen, currentScreen } = selectState;
    return {
      position: "absolute",
      left: Math.min(startScreen[0], currentScreen[0]),
      top: Math.min(startScreen[1], currentScreen[1]),
      width: Math.abs(currentScreen[0] - startScreen[0]),
      height: Math.abs(currentScreen[1] - startScreen[1]),
      border: "1px dashed #3b82f6",
      background: "rgba(59,130,246,0.08)",
      pointerEvents: "none",
    };
  }, [selectState]);

  // ---- Render ----
  const view = useMemo(() => new OrthographicView({ id: "ortho", flipY: true }), []);

  return (
    <div
      ref={containerRef}
      style={{ width: "100%", height: "100%", position: "relative", overflow: "hidden" }}
      onContextMenu={handleContextMenu}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onContextMenuCapture={(e) => e.preventDefault()}
    >
      <DeckGL
        ref={deckRef}
        views={view}
        viewState={viewState}
        onViewStateChange={handleViewStateChange as any}
        controller={controllerOptions}
        layers={layers}
        onHover={handleHover}
        onClick={handleDeckClick as any}
        style={{
          position: "absolute",
          top: "0",
          left: "0",
          right: "0",
          bottom: "0",
          background: "#ffffff",
        }}
        parameters={{ depthTest: false, depthMask: false } as any}
      />

      {/* Selection rect overlay */}
      {selectRectStyle && <div style={selectRectStyle} />}

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            position: "absolute",
            left: tooltip.x,
            top: tooltip.y,
            background: "#ffffff",
            border: "1px solid #999",
            borderRadius: 3,
            padding: "4px 8px",
            fontSize: 12,
            pointerEvents: "none",
            whiteSpace: "pre-line",
            zIndex: 10,
            maxWidth: 260,
            boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
          }}
        >
          {tooltip.texts.join("\n")}
        </div>
      )}

      {/* Measure distance label */}
      {measureLabelScreen && (
        <div
          style={{
            position: "absolute",
            left: measureLabelScreen.x,
            top: measureLabelScreen.y - 22,
            transform: "translateX(-50%)",
            background: "rgba(0,0,0,0.75)",
            color: "#fff",
            borderRadius: 3,
            padding: "2px 7px",
            fontSize: 12,
            pointerEvents: "none",
            zIndex: 10,
          }}
        >
          {measureLabelScreen.dist.toFixed(2)} m
        </div>
      )}

      {/* Context menu */}
      {contextMenu && (
        <div
          style={{
            position: "absolute",
            left: contextMenu.x,
            top: contextMenu.y,
            background: "#EEEEEE",
            border: "1px solid #999",
            borderRadius: 4,
            minWidth: 150,
            zIndex: 20,
            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
          }}
        >
          {contextMenu.polygon.rightClickMenu?.map((label) => (
            <div
              key={label}
              style={{
                padding: "6px 12px",
                cursor: "pointer",
                fontSize: 14,
                userSelect: "none",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLDivElement).style.background = "#ddd";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLDivElement).style.background = "";
              }}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => {
                onRightMenuClick?.(label, contextMenu.polygon);
                setContextMenu(null);
              }}
            >
              {label}
            </div>
          ))}
        </div>
      )}

      {/* Pointer tracker circle */}
      {pointerTracker && pointerPos && (
        <div
          style={{
            position: "absolute",
            left: pointerPos[0] - pointerTracker.size / 2,
            top: pointerPos[1] - pointerTracker.size / 2,
            width: pointerTracker.size,
            height: pointerTracker.size,
            borderRadius: "50%",
            background: pointerTracker.color,
            pointerEvents: "none",
            opacity: 0.6,
            zIndex: 5,
          }}
        />
      )}
    </div>
  );
};

export default PixBimViewerV2;
